import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from "framer-motion";
import { Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next'; 
import { useAuth } from '../context/AuthContext';
import GeolocationCheck from './GeolocationCheck';

import FaseTipoServicio from './reserva/FaseTipoServicio';
import FaseFechaHora from './reserva/FaseFechaHora';
import FaseRecogida from './reserva/FaseRecogida';
import FaseDestino from './reserva/FaseDestino';
import FasePago from './reserva/FasePago';
import FaseConfirmacion from './reserva/FaseConfirmacion';

const fases = [
  'tipoServicio',
  'fechaHora',
  'recogida',
  'destino',
  'pago',
  'confirmacion'
];

const ReservaWizard = () => {
  const { i18n } = useTranslation(); // <-- Añade esta línea
  const { isAuthenticated, loginAsAnonymous, user, accessToken } = useAuth();
  const [faseActual, setFaseActual] = useState(0);
  const [datosReserva, setDatosReserva] = useState({});
  const [direccion, setDireccion] = useState("next");
  const [geolocationGranted, setGeolocationGranted] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const location = useLocation();

  // Autenticar como usuario anónimo si no está autenticado
  useEffect(() => {
    const authenticateIfNeeded = async () => {
      if (!isAuthenticated) {
        console.log("[ReservaWizard] Usuario no autenticado. Autenticando como anónimo...");
        setIsAuthenticating(true);
        const success = await loginAsAnonymous();
        if (!success) {
          console.error("[ReservaWizard] No se pudo autenticar como usuario anónimo");
        }
        setIsAuthenticating(false);
      } else if (user?.isAnonymous) {
        console.log("[ReservaWizard] Usuario anónimo ya autenticado:", user.email);
        console.log("[ReservaWizard] Token disponible:", accessToken ? "Sí" : "No");
        setIsAuthenticating(false);
      } else {
        console.log("[ReservaWizard] Usuario normal autenticado:", user.email);
        setIsAuthenticating(false);
      }
    };

    authenticateIfNeeded();
  }, [isAuthenticated, loginAsAnonymous, user, accessToken]);

  // Maneja el reset y la redirección desde la pasarela
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reservaId = params.get("reserva_id");
    const estado = params.get("estado");
    
    // Si hay reserva_id y estado en la URL, salta a la fase de confirmación
    if (reservaId && estado) {
      console.log("[ReservaWizard] Detectado reserva_id y estado en URL. Saltando a confirmación."); 
      setDatosReserva({ reserva_id: reservaId, reservaId, estado });
      setFaseActual(fases.indexOf('confirmacion'));
    } else {
      // Solo reinicia si no hay parámetros de confirmación
      console.log("[ReservaWizard] Reiniciando wizard.");
      setFaseActual(0);
      setDatosReserva({});
    }
  }, [location.pathname, location.search]);

  const retroceder = (destino) => {
    setDireccion("back");
    if (destino === "inicio") {
      setFaseActual(0); // Llevar al inicio (fase inicial)
    } else {
      const indiceDestino = fases.indexOf(destino);
      if (indiceDestino !== -1) {
        setFaseActual(indiceDestino); // Cambiar a la fase específica
      } else {
        setFaseActual((prev) => prev - 1); // Retroceder a la fase anterior si no se encuentra el destino
      }
    }
  };

  const handleNext = (data) => {
    console.log("[ReservaWizard] [handleNext] Datos recibidos en handleNext:", data); // Depuración
    console.log("[ReservaWizard] [handleNext] Nombre de fase actual antes de modificar:", fases[faseActual]); // Depuración

    setDatosReserva((prev) => ({ ...prev, ...data })); // Actualiza datosReserva con los nuevos datos

    setFaseActual((prev) => {
      const nuevaFase = prev + 1;
      console.log("[ReservaWizard] Índice de nueva fase calculada:", nuevaFase); // Depuración
      console.log("[ReservaWizard] Nombre de nueva fase calculada:", fases[nuevaFase]); // Depuración

      // Validar que la fase actual sea correcta antes de avanzar a 'confirmacion'
      //if (fases[nuevaFase] === 'confirmacion') {
      //  if (!data.reserva_id || !data.estado) {
      //    console.error("[ReservaWizard] Intentando avanzar a confirmación sin reserva_id o estado. Deteniendo flujo.");
      //    return prev; // No avanzar si los datos son incompletos
      //  }
      //}

      console.log("[ReservaWizard] Cambiando a la fase:", fases[nuevaFase]); // Depuración
      return nuevaFase;
    });
  };

  const handleRetry = () => {
    console.log("[ReservaWizard] Reintentando el pago...");
    setFaseActual(fases.indexOf('pago')); // Regresa a la fase de pago
  };

  const handleGeolocationGranted = () => {
    console.log('[ReservaWizard] Geolocalización concedida');
    setGeolocationGranted(true);
  };

  const handleGeolocationDenied = () => {
    console.log('[ReservaWizard] Geolocalización denegada');
    setGeolocationGranted(false);
  };

  const variants = {
    enter: (direction) => ({
      x: direction === "next" ? 300 : -300,
      opacity: 0,
      position: "absolute",
    }),
    center: { x: 0, opacity: 1, position: "relative" },
    exit: (direction) => ({
      x: direction === "next" ? -300 : 300,
      opacity: 0,
      position: "absolute",
    }),
  };

  const renderFaseActual = () => {
    console.log("[ReservaWizard] Renderizando fase actual:", fases[faseActual]); // Depuración
    switch (fases[faseActual]) {
      case 'tipoServicio':
        return <FaseTipoServicio onNext={handleNext} />;
      case 'fechaHora':
        return <FaseFechaHora onNext={handleNext} onBack={retroceder} tipoServicio={datosReserva.tipoServicio} />;
      case 'recogida':
        return (
          <FaseRecogida
            onNext={handleNext}
            onBack={retroceder}
            tipoServicio={datosReserva.tipoServicio}
            posicionInicial={datosReserva.puntoRecogida}
          />
        );
      case 'destino':
        return (
          <FaseDestino
            onNext={handleNext}
            onBack={retroceder}
            tipoServicio={datosReserva.tipoServicio}
            posicionInicial={datosReserva.puntoDestino}
          />
        );
      case 'pago':
        return <FasePago onBack={retroceder} onNext={handleNext} datosReserva={datosReserva} />;
      case 'confirmacion':
        return <FaseConfirmacion datosReserva={datosReserva} onRetry={handleRetry} onBack={retroceder} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    console.log("[ReservaWizard] Estado de faseActual:", faseActual); // Depuración
    console.log("[ReservaWizard] Estado de datosReserva:", datosReserva); // Depuración
    console.log("[ReservaWizard] Estado de geolocationGranted:", geolocationGranted); // Depuración
  }, [faseActual, datosReserva, geolocationGranted]);

  return (
    <Box position="relative" minH="400px">
      <GeolocationCheck 
        onGeolocationGranted={handleGeolocationGranted}
        onGeolocationDenied={handleGeolocationDenied}
      />
      
      {isAuthenticating ? (
        <Box textAlign="center" py={10}>
          <p>Autenticando...</p>
        </Box>
      ) : (
        <AnimatePresence custom={direccion} initial={false} mode="wait">
          <motion.div
            key={faseActual + '-' + i18n.language}
            custom={direccion}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4, type: "tween" }}
            style={{ 
              width: "100%", 
              pointerEvents: geolocationGranted ? 'auto' : 'none',
              opacity: geolocationGranted ? 1 : 0.3
            }}
          >
            {/* Aquí renderiza la fase actual */}
            {renderFaseActual()}
          </motion.div>
        </AnimatePresence>
      )}
    </Box>
  );
};

export default ReservaWizard;